/**
 * Auto-close clock for toasts. Kept apart from `Toast.tsx` because it must not touch
 * `sonner` (which injects its stylesheet at import time and needs a DOM) nor JSX.
 *
 * Why we run the clock instead of handing sonner a `duration`: sonner 2.0.7 pauses a
 * toast's own countdown while `expanded`, `interacting` or `document.hidden` is true, and
 * resumes it only on the matching mouse / visibility event. In a WebView those events do
 * not reliably arrive — a touch on the card that turns into a scroll fires `pointercancel`
 * and never `pointerup`, so `interacting` stays true for the rest of the mount and freezes
 * every later toast with it. The library has no way out and no fix upstream: 2.0.8 (latest)
 * still has zero `pointercancel` handlers, and `pauseWhenPageIsHidden` — which used to
 * guard the hidden-document pause in 1.7.x — was dropped in 2.0, so that pause is now
 * unconditional. A toast therefore goes out with `duration: Infinity`, which makes sonner
 * install no timer of its own, and dies on this clock.
 *
 * Holding a toast to read it is kept — it is the only thing explaining a refusal — but the
 * hold is ours: every path that ends a touch releases it, and `MAX_HOLD_MS` caps how long
 * a stuck one can matter.
 */

type TimeoutHandle = ReturnType<typeof setTimeout>;

/**
 * Сколько сверх своей длительности может прожить удержанная карточка. Было 8 с при
 * статусе в 12 — то есть до 20 с на экране. Теперь 4: удержание нужно, чтобы дочитать
 * после того, как отвлёкся, а на дочитывание хватает половины времени чтения самой
 * длинной карточки (19 слов ≈ 6,3 с). Потолок при этом остаётся страховкой от зависшего
 * удержания, а не способом продлить чтение вдвое: худший случай упал с 20 с до 13.
 */
export const MAX_HOLD_MS = 4000;

interface Entry {
  dismiss: (id: string) => void;
  /** Milliseconds still to tick once running. */
  remaining: number;
  startedAt: number;
  /** `null` while held. */
  timer: TimeoutHandle | null;
  ceiling: TimeoutHandle;
}

const entries = new Map<string, Entry>();

let held = false;

let counter = 0;

/**
 * Id for a toast the caller did not name. An explicit id is mandatory, not cosmetic:
 * `toast.custom(jsx, { id: undefined, … })` returns one counter value while the toast is
 * created with the next one, so the returned id dismisses nothing.
 */
export const nextToastId = () => `toast-${++counter}`;

const drop = (id: string) => {
  const entry = entries.get(id);
  if (entry === undefined) return undefined;
  if (entry.timer !== null) clearTimeout(entry.timer);
  clearTimeout(entry.ceiling);
  entries.delete(id);
  return entry;
};

const fire = (id: string) => {
  const entry = drop(id);
  entry?.dismiss(id);
};

const run = (id: string, entry: Entry) => {
  entry.startedAt = Date.now();
  entry.timer = setTimeout(() => fire(id), entry.remaining);
};

/**
 * Arms the close of `id` in `duration` ms. Re-arming the same id drops the pending close
 * and starts the full duration over, so a status toast that replaces itself gets its whole
 * lifetime instead of expiring on the previous one's clock.
 */
export const scheduleAutoDismiss = (id: string, duration: number, dismiss: (id: string) => void) => {
  cancelAutoDismiss(id);
  if (!Number.isFinite(duration)) return;
  listen();
  const entry: Entry = {
    dismiss,
    remaining: duration,
    startedAt: Date.now(),
    timer: null,
    ceiling: setTimeout(() => fire(id), duration + MAX_HOLD_MS),
  };
  entries.set(id, entry);
  if (!held) run(id, entry);
};

/** Drops a pending close — the toast is already gone (swiped away, or dismissed by code). */
export const cancelAutoDismiss = (id: string) => {
  drop(id);
};

/** Holds every countdown while the card is being read — finger on it, cursor over it. */
export const pauseAutoDismiss = () => {
  if (held) return;
  held = true;
  for (const entry of entries.values()) {
    if (entry.timer === null) continue;
    clearTimeout(entry.timer);
    entry.timer = null;
    entry.remaining = Math.max(0, entry.remaining - (Date.now() - entry.startedAt));
  }
};

/** Releases the hold and resumes each countdown from where it stopped. */
export const resumeAutoDismiss = () => {
  if (!held) return;
  held = false;
  for (const [id, entry] of entries) run(id, entry);
};

const overToast = (target: EventTarget | null) => target instanceof Element && target.closest('[data-sonner-toast]') !== null;

let listening = false;

/**
 * Installed on first use rather than at import: this module is imported by node-side tests
 * and by SSR, where there is no `document`.
 */
const listen = () => {
  if (listening || typeof document === 'undefined') return;
  listening = true;

  document.addEventListener('pointerdown', (event) => { if (overToast(event.target)) pauseAutoDismiss(); }, true);

  // Every way a touch can end, not just the tidy one. A gesture that turns into a scroll
  // sends `pointercancel` and no `pointerup`, and that single missing event is what leaves
  // sonner's own pause on forever.
  document.addEventListener('pointerup', resumeAutoDismiss, true);
  document.addEventListener('pointercancel', resumeAutoDismiss, true);
  document.addEventListener('lostpointercapture', resumeAutoDismiss, true);

  // Hover holds only for a real mouse: a tap also emits compatibility mouse events, and
  // the matching `mouseout` may never come — the same trap one layer down.
  document.addEventListener('pointerover', (event) => {
    if (event.pointerType === 'mouse' && overToast(event.target)) pauseAutoDismiss();
  }, true);
  document.addEventListener('pointerout', (event) => {
    if (event.pointerType !== 'mouse' || overToast(event.relatedTarget)) return;
    resumeAutoDismiss();
  }, true);
};
